import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { MediaPreview } from "../../../components/dashboard/MediaPreview";
import { Modal } from "../../../components/dashboard/Modal";
import { useConfirm } from "../../../contexts/ConfirmContext";
import { useToast } from "../../../contexts/ToastContext";
import { extractErrorMessage } from "../../../lib/api";
import {
  deletePlaylist,
  getPlaylist,
  listMedia,
  listPlaylists,
  savePlaylist,
  uploadMedia,
} from "../../../lib/dashboardApi";
import { formatDuration } from "../../../lib/format";
import { hasMinRole } from "../../../lib/hooks/useAuthMe";
import type { MediaItem, Playlist, PlaylistItem, User } from "../../../types/dashboard";

type Ctx = { user?: User };

const DEFAULT_DURATION = 10;

// Videos play their full natural length on the player (duration 0 means
// "until ended"), so they carry no editable seconds value.
function isVideo(m?: MediaItem) {
  return !!m?.mime_type?.startsWith("video");
}

export function Playlists() {
  const { user } = useOutletContext<Ctx>();
  const canEdit = hasMinRole(user, "editor");
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Playlist> | null>(null);
  const [items, setItems] = useState<PlaylistItem[]>([]);
  const [search, setSearch] = useState("");
  const [partnerOpen, setPartnerOpen] = useState(false);

  const { data: list = [], isLoading } = useQuery({
    queryKey: ["playlists"],
    queryFn: listPlaylists,
  });

  // Client-side filter across the fields shown on each card: playlist name,
  // group (client) name, and campaign.
  const query = search.trim().toLowerCase();
  const filtered = query
    ? list.filter((p) =>
        [p.name, p.client_name, p.campaign].some((f) => f?.toLowerCase().includes(query)),
      )
    : list;
  const partnerPlaylists = list.filter((p) => p.is_partner);
  const { data: media = [] } = useQuery({
    queryKey: ["media", { all: true }],
    queryFn: () => listMedia(),
  });

  function openCreate() {
    setEditing({ name: "", client_name: "", campaign: "" });
    setItems([]);
    setModalOpen(true);
  }
  async function openEdit(p: Playlist) {
    // Open immediately with summary fields so the modal feels responsive,
    // then hydrate items from the detail endpoint. The list endpoint
    // returns PlaylistSummary (no items array), so without this fetch
    // re-opening a saved playlist shows zero items even though the
    // backend has them.
    setEditing({ ...p });
    setItems(
      [...(p.items ?? [])]
        .sort((a, b) => a.order - b.order)
        .map((it) => ({ ...it })),
    );
    setModalOpen(true);
    try {
      const full = await getPlaylist(p.id);
      setEditing({ ...full });
      setItems(
        [...(full.items ?? [])]
          .sort((a, b) => a.order - b.order)
          .map((it) => ({ ...it })),
      );
    } catch (e) {
      toast(extractErrorMessage(e, "Could not load playlist."), "error");
    }
  }

  async function save() {
    if (!editing?.name?.trim()) {
      toast("Playlist name is required.", "error");
      return;
    }
    try {
      // Playlists saved before videos became full-length may still carry a
      // seconds value on video items — normalize so they play to the end.
      const normalized = items.map((it) =>
        isVideo(media.find((m) => m.id === it.media_id)) ? { ...it, duration: 0 } : it,
      );
      await savePlaylist({ ...editing, items: normalized });
      qc.invalidateQueries({ queryKey: ["playlists"] });
      toast(editing.id ? "Playlist updated." : "Playlist created.");
      setModalOpen(false);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not save."), "error");
    }
  }

  async function remove(p: Playlist) {
    if (
      !(await confirm({
        title: "Delete playlist?",
        message: `Delete "${p.name}"?`,
        danger: true,
      }))
    )
      return;
    try {
      await deletePlaylist(p.id);
      qc.invalidateQueries({ queryKey: ["playlists"] });
      toast("Deleted.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not delete."), "error");
    }
  }

  return (
    <>
      <h2 className="page-title">Playlists</h2>
      <div className="card">
        <div className="card-header">
          <h3>
            All Playlists{" "}
            <span style={{ fontWeight: 400, color: "var(--text2)", fontSize: 13 }}>
              {filtered.length}
            </span>
          </h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {list.length > 0 && (
              <input
                className="form-input list-search"
                type="search"
                placeholder="Search playlists…"
                aria-label="Search playlists"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setPartnerOpen(true)}
            >
              Partner Playlists{" "}
              <span style={{ opacity: 0.7 }}>{partnerPlaylists.length}</span>
            </button>
            {canEdit && (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                + New Playlist
              </button>
            )}
          </div>
        </div>
        {isLoading ? (
          <div className="empty">Loading…</div>
        ) : list.length === 0 ? (
          <div className="empty">No playlists yet. Create one to get started.</div>
        ) : filtered.length === 0 ? (
          <div className="empty">No playlists match “{search.trim()}”.</div>
        ) : (
          <div className="playlists-grid">
            {filtered.map((p) => {
              const thumb =
                p.thumbnail_url ??
                media.find((m) => m.id === p.items?.[0]?.media_id)?.url ??
                null;
              return (
                <div
                  key={p.id}
                  className="pl-card"
                  onClick={() => canEdit && openEdit(p)}
                  role={canEdit ? "button" : undefined}
                  tabIndex={canEdit ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (canEdit && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      openEdit(p);
                    }
                  }}
                >
                  <div className="pl-card-thumb">
                    {thumb ? (
                      p.thumbnail_type === "video" ? (
                        <video src={thumb} muted preload="metadata" />
                      ) : (
                        <img src={thumb} alt="" />
                      )
                    ) : (
                      <span className="pl-no-thumb">♪</span>
                    )}
                    <span className="pl-item-badge">{p.item_count ?? p.items?.length ?? 0}</span>
                  </div>
                  <div className="pl-card-body">
                    <div className="pl-card-name">
                      {p.name}
                      {p.is_partner && (
                        <span className="badge badge-online" style={{ marginLeft: 6 }}>
                          Partner
                        </span>
                      )}
                    </div>
                    <div className="pl-card-client">
                      {p.client_name ?? "—"}
                      {p.campaign ? ` · ${p.campaign}` : ""}
                    </div>
                    <div className="pl-card-stats">
                      {/* Videos play full length (duration 0), so a playlist
                          of only videos has no meaningful total. */}
                      <span>{p.total_duration ? formatDuration(p.total_duration) : "—"}</span>
                    </div>
                    {canEdit && (
                      <div className="pl-card-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(p);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(p);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <PlaylistModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        setEditing={setEditing}
        items={items}
        setItems={setItems}
        media={media}
        onSave={save}
        canEdit={canEdit}
      />
      <Modal
        open={partnerOpen}
        onClose={() => setPartnerOpen(false)}
        width={460}
        ariaLabel="Partner playlists"
      >
        <h3>Partner Playlists</h3>
        <p className="pw-hint" style={{ marginTop: 0 }}>
          Mark a playlist as “Partner” in its editor to add it here. From a screen
          group you can add all of these in one click.
        </p>
        {partnerPlaylists.length === 0 ? (
          <div className="empty" style={{ padding: 16 }}>
            No partner playlists yet.
          </div>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {partnerPlaylists.map((p) => (
              <li
                key={p.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <span>{p.name}</span>
                {canEdit && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={() => {
                      setPartnerOpen(false);
                      openEdit(p);
                    }}
                  >
                    Edit
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setPartnerOpen(false)}
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  );
}

function PlaylistModal({
  open,
  onClose,
  editing,
  setEditing,
  items,
  setItems,
  media,
  onSave,
  canEdit,
}: {
  open: boolean;
  onClose: () => void;
  editing: Partial<Playlist> | null;
  setEditing: (p: Partial<Playlist> | null) => void;
  items: PlaylistItem[];
  setItems: (it: PlaylistItem[]) => void;
  media: MediaItem[];
  onSave: () => void;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [fileDragging, setFileDragging] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  // dragenter/dragleave fire for every child element the cursor crosses, so a
  // depth counter is the only reliable way to know when the file has truly
  // left the modal (vs. just moved onto a nested element).
  const dragDepth = useRef(0);

  // Only react to OS file drags. The playlist's own reorder drag (a plain
  // HTML5 element drag) carries no "Files" type, so this cleanly ignores it.
  function isFileDrag(e: React.DragEvent) {
    return Array.from(e.dataTransfer.types ?? []).includes("Files");
  }
  function onFileDragEnter(e: React.DragEvent) {
    if (!canEdit || uploading || !isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setFileDragging(true);
  }
  function onFileDragOver(e: React.DragEvent) {
    if (!canEdit || uploading || !isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function onFileDragLeave(e: React.DragEvent) {
    if (!isFileDrag(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setFileDragging(false);
  }
  function onFileDrop(e: React.DragEvent) {
    if (!canEdit || uploading || !isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setFileDragging(false);
    handleInlineUpload(e.dataTransfer.files);
  }

  async function handleInlineUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadPct(0);
    try {
      const uploaded = await uploadMedia(Array.from(files), undefined, (p) =>
        setUploadPct(p.percent),
      );
      qc.invalidateQueries({ queryKey: ["media"] });
      // Auto-append the uploaded items to the current playlist. Saves
      // the user a second click on the picker grid — the typical flow
      // (upload, then add to this playlist) becomes one step.
      const appended = uploaded.map((m, i) => ({
        media_id: m.id,
        duration: isVideo(m) ? 0 : DEFAULT_DURATION,
        order: items.length + i,
      }));
      setItems([...items, ...appended]);
      toast(
        `Uploaded ${uploaded.length} file${uploaded.length === 1 ? "" : "s"}; added to playlist.`,
      );
    } catch (e) {
      toast(extractErrorMessage(e, "Upload failed."), "error");
    } finally {
      setUploading(false);
    }
  }

  if (!editing) return null;

  function addMedia(m: MediaItem) {
    const nextOrder = items.length;
    setItems([
      ...items,
      { media_id: m.id, duration: isVideo(m) ? 0 : DEFAULT_DURATION, order: nextOrder },
    ]);
    // The order list is below the fold; confirm the add so the user doesn't
    // have to scroll down to verify it landed.
    toast(`Added ${m.original_name} to playlist.`);
  }
  // Picker toggle: drop every occurrence of this media from the playlist.
  function removeMedia(m: MediaItem) {
    setItems(
      items.filter((it) => it.media_id !== m.id).map((it, i) => ({ ...it, order: i })),
    );
  }
  function removeAt(idx: number) {
    setItems(items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i })));
  }
  function setDuration(idx: number, dur: number) {
    setItems(items.map((it, i) => (i === idx ? { ...it, duration: dur } : it)));
  }

  function onDragStart(idx: number) {
    dragIndexRef.current = idx;
  }
  function onDragOver(idx: number, e: React.DragEvent) {
    e.preventDefault();
    setDragOverIdx(idx);
  }
  function onDrop(idx: number, e: React.DragEvent) {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === idx) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setItems(next.map((it, i) => ({ ...it, order: i })));
    dragIndexRef.current = null;
    setDragOverIdx(null);
  }

  return (
    <Modal open={open} onClose={onClose} width={580} ariaLabel="Playlist editor">
      <div
        className="playlist-dropzone"
        onDragEnter={onFileDragEnter}
        onDragOver={onFileDragOver}
        onDragLeave={onFileDragLeave}
        onDrop={onFileDrop}
      >
        {fileDragging && (
          <div className="playlist-drop-overlay" aria-hidden="true">
            <div className="playlist-drop-overlay-inner">
              <div style={{ fontSize: 32 }}>⤓</div>
              <div>Drop media to upload &amp; add to this playlist</div>
            </div>
          </div>
        )}
        <h3>{editing.id ? "Edit Playlist" : "New Playlist"}</h3>
      <div className="form-group">
        <label>Playlist Name</label>
        <input
          className="form-input"
          value={editing.name ?? ""}
          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          autoFocus
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Group name (for reports)</label>
          <input
            className="form-input"
            value={editing.client_name ?? ""}
            onChange={(e) => setEditing({ ...editing, client_name: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Campaign / Ad name</label>
          <input
            className="form-input"
            value={editing.campaign ?? ""}
            onChange={(e) => setEditing({ ...editing, campaign: e.target.value })}
          />
        </div>
      </div>
      <div className="form-group">
        <label
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={editing.is_partner ?? false}
            onChange={(e) => setEditing({ ...editing, is_partner: e.target.checked })}
          />
          <span>Partner playlist</span>
        </label>
        <p className="pw-hint" style={{ margin: "4px 0 0" }}>
          Partner playlists can be bulk-added to a screen group in one click.
        </p>
      </div>
      <div className="form-group">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <label style={{ margin: 0 }}>
            Add Media{" "}
            <span style={{ fontWeight: 400, color: "var(--text2)" }}>
              (click to add, or drag files here)
            </span>
          </label>
          {canEdit && (
            <>
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  handleInlineUpload(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={uploading}
                onClick={() => uploadInputRef.current?.click()}
              >
                {uploading ? `Uploading… ${uploadPct}%` : "+ Upload new"}
              </button>
            </>
          )}
        </div>
        <div
          className="media-grid picker-grid"
          style={{
            maxHeight: 200,
            overflowY: "auto",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: 8,
          }}
        >
          {media.length === 0 ? (
            <div className="empty" style={{ gridColumn: "1 / -1" }}>
              No media yet — upload some first.
            </div>
          ) : (
            media.map((m) => {
              const inList = items.some((it) => it.media_id === m.id);
              return (
                <div
                  key={m.id}
                  className={inList ? "media-item selected" : "media-item"}
                  onClick={() => addMedia(m)}
                >
                  {m.mime_type?.startsWith("video") ? (
                    <video src={m.url} muted />
                  ) : (
                    <img src={m.url} alt={m.original_name} />
                  )}
                  <button
                    type="button"
                    className="select-check"
                    aria-pressed={inList}
                    aria-label={
                      inList
                        ? `Remove ${m.original_name} from playlist`
                        : `Add ${m.original_name} to playlist`
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (inList) removeMedia(m);
                      else addMedia(m);
                    }}
                  >
                    {inList ? "✓" : "+"}
                  </button>
                  <button
                    type="button"
                    className="preview-btn"
                    aria-label={`Preview ${m.original_name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreview(m);
                    }}
                  >
                    ⤢
                  </button>
                  <div className="overlay">{m.original_name}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <div className="form-group">
        <label>
          Playlist Order{" "}
          <span className="playlist-count-badge">{items.length}</span>{" "}
          <span style={{ fontWeight: 400, color: "var(--text2)", fontSize: 11 }}>
            drag to reorder
          </span>
        </label>
        {items.length === 0 ? (
          <div className="empty" style={{ padding: 20, fontSize: 13 }}>
            Click media above to add items to this playlist.
          </div>
        ) : (
          <ul className="playlist-items">
            {items.map((it, idx) => {
              const m = media.find((mm) => mm.id === it.media_id);
              return (
                <li
                  key={`${it.media_id}-${idx}`}
                  className={
                    dragOverIdx === idx ? "playlist-item dragging" : "playlist-item"
                  }
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={(e) => onDragOver(idx, e)}
                  onDrop={(e) => onDrop(idx, e)}
                  onDragEnd={() => {
                    dragIndexRef.current = null;
                    setDragOverIdx(null);
                  }}
                >
                  <span className="grip" aria-hidden="true">⋮⋮</span>
                  {m ? (
                    <button
                      type="button"
                      className="info info-link"
                      onClick={() => setPreview(m)}
                      title="Preview"
                    >
                      {m.original_name}
                    </button>
                  ) : (
                    <span className="info">{`Media #${it.media_id}`}</span>
                  )}
                  {isVideo(m) ? (
                    <span
                      style={{ fontSize: 11, color: "var(--text2)", whiteSpace: "nowrap" }}
                      title="Videos play their full length"
                    >
                      Full video
                    </span>
                  ) : (
                    <>
                      <input
                        type="number"
                        className="form-input dur-input"
                        min={1}
                        max={3600}
                        value={it.duration}
                        aria-label="Duration (seconds)"
                        onChange={(e) => setDuration(idx, Math.max(1, Number(e.target.value)))}
                      />
                      <span style={{ fontSize: 11, color: "var(--text2)" }}>s</span>
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    aria-label="Remove item"
                    onClick={() => removeAt(idx)}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={onSave}>
            Save Playlist
          </button>
        </div>
        <MediaPreview item={preview} onClose={() => setPreview(null)} />
      </div>
    </Modal>
  );
}
